import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { App } from "@/lib/capacitor/app";
import { useAuthType } from "@/lib/hooks/use-auth-type";
import { resolveDeeplinkPath } from "@/lib/router/deeplink";
import { logger } from "@/logger";

export function AppUrlListener() {
  const history = useHistory();
  const { isLoading: isAuthLoading, isLoadingMe } = useAuthType();
  const [isRouterReady, setIsRouterReady] = useState(false);
  const [isAppReady, setIsAppReady] = useState(false);
  const listenerHandleRef = useRef<{ remove: () => void } | null>(null);
  const pendingUrlsRef = useRef<string[]>([]);

  // Mark router as ready after initial render
  useEffect(() => {
    setIsRouterReady(true);
  }, []);

  // Mark app as fully ready when both router and auth are ready
  // Always wait for auth to load - deeplinks need fresh session data
  // Also wait for me query to complete to ensure we have full auth state
  useEffect(() => {
    if (isRouterReady && !isAuthLoading && !isLoadingMe) {
      // Delay to ensure auth state has fully propagated to all components
      // This prevents race conditions where navigation happens before
      // isSignedIn/isEmployee/isAdmin values are available
      const timer = setTimeout(() => {
        logger.info("[AppUrlListener] App is ready for navigation", {
          hasPendingUrls: pendingUrlsRef.current.length > 0,
        });
        setIsAppReady(true);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [isRouterReady, isAuthLoading, isLoadingMe]);

  // Helper function reference - will be populated by useEffect below
  const processUrl = useRef<(urlString: string) => void>(() => {
    // This will be replaced by the useEffect below
  });

  // Update processUrl ref when dependencies change
  useEffect(() => {
    processUrl.current = (urlString: string) => {
      try {
        // Waar de link heen wijst, en waarom die reconstructie nodig is: `lib/router/deeplink.ts`.
        const target = resolveDeeplinkPath(urlString);
        if (!target) {
          logger.error({ url: urlString }, "Failed to parse URL");
          return;
        }

        const { path, search: searchParams, hash } = target;
        logger.info({ originalUrl: urlString, path }, "Resolved deep link to app path");

        if (!isAppReady) {
          // Add to queue instead of overwriting
          if (!pendingUrlsRef.current.includes(urlString)) {
            pendingUrlsRef.current.push(urlString);
            logger.debug(
              { path, isRouterReady, isAuthLoading, queueLength: pendingUrlsRef.current.length },
              "App not ready for navigation, queuing URL",
            );
          }
          return;
        }

        logger.info({ path, searchParams, hash }, "Navigating to deep link");

        try {
          history.push({
            pathname: path,
            search: searchParams,
            hash: hash,
          });

          // Hide splash screen after successful navigation on native platforms
          if (Capacitor.isNativePlatform()) {
            SplashScreen.hide().catch((error) => {
              logger.error({ error }, "Failed to hide splash screen");
            });
          }
        } catch (navError) {
          logger.error({ error: navError, path }, "Navigation failed - recovering");

          // Always try to hide splash screen even on navigation error
          if (Capacitor.isNativePlatform()) {
            SplashScreen.hide().catch((error) => {
              logger.error({ error }, "Failed to hide splash screen after nav error");
            });
          }
        }
      } catch (error) {
        logger.error({ error, url: urlString }, "Failed to process URL");
      }
    };
  }, [history, isAppReady, isRouterReady, isAuthLoading, isLoadingMe]);

  // Handle cold start launch URL - only run once
  useEffect(() => {
    async function handleLaunchUrl() {
      const launchUrl = await App.getLaunchUrl();
      if (launchUrl?.url) {
        logger.info(
          { launchUrl: launchUrl.url, isAuthLoading },
          "Handling launch URL (cold start)",
        );
        processUrl.current(launchUrl.url);

        // Don't hide splash screen yet - wait for auth to load and navigation to complete
        // Splash screen will be hidden after navigation in processUrl
      }
    }

    handleLaunchUrl();
  }, []); // Run only once on mount

  // Setup listener for URL opens while app is running - only run once
  useEffect(() => {
    App.addUrlOpenListener((event) => {
      logger.debug(event, "Open App Url");
      processUrl.current(event.url);
    }).then((handle) => {
      listenerHandleRef.current = handle;
    });

    return () => {
      // Remove only our listener, not all listeners
      if (listenerHandleRef.current) {
        listenerHandleRef.current.remove();
      }
    };
  }, []); // Run only once on mount

  // Process all pending URLs once app is fully ready
  useEffect(() => {
    if (isAppReady && pendingUrlsRef.current.length > 0) {
      logger.info(
        { count: pendingUrlsRef.current.length, urls: pendingUrlsRef.current },
        "Processing pending URLs after app ready",
      );

      // Process all queued URLs in order
      const urlsToProcess = [...pendingUrlsRef.current];
      pendingUrlsRef.current = [];

      urlsToProcess.forEach((urlString) => {
        processUrl.current(urlString);
      });
    }
  }, [isAppReady]);

  return null;
}
